import { groupBy, mapValues, uniq } from 'es-toolkit';
import React, { useState } from 'react';

import { PropertySelectField } from '@paragon/client-tools';
import { PlateManufacturer, PlatePair } from '@paragon/contract-library';
import { getPlateManufacturer } from '@paragon/material-lists';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { sortBy } from '@paragon/utilities';

import { generateDigitalQcFileForActiveComponentDesign } from '../../store/actions/downloadActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { useAppDispatch, useAppSelector } from '../hooks';

const anyPlatePlateManufacturers: readonly PlateManufacturer[] = ['Alpine', 'CH Machine', 'Eagle', 'MiTek', 'Simpson'];

export function QualityControlDigitalQcDownloadDialog() {
  const dispatch = useAppDispatch();

  const activeComponentDesign = useAppSelector(getActiveComponentDesign);

  const anyPlatePlatePairs = activeComponentDesign
    ? activeComponentDesign.component.platePairs.filter(
        (platePair) => getPlateManufacturer(platePair.plateType) === 'Any',
      )
    : [];
  const comboPlatePlatePairs = activeComponentDesign
    ? activeComponentDesign.component.platePairs.filter(
        (platePair) => getPlateManufacturer(platePair.plateType) === 'Combo',
      )
    : [];
  function getComboPlateTangiblePlateTypesGroupKey(platePair: PlatePair) {
    return sortBy(platePair.tangiblePlateTypesForComboPlateType, (plateType) => plateType).join('-');
  }
  const groupedTangiblePlateTypesForComboPlates = groupBy(
    comboPlatePlatePairs,
    getComboPlateTangiblePlateTypesGroupKey,
  );
  const orderedGroupedTangiblePlateTypesForComboPlates = sortBy(
    Object.entries(groupedTangiblePlateTypesForComboPlates),
    ([, group]) => group.length,
    'desc',
  );

  const [anyPlatePlateManufacturer, setAnyPlatePlateManufacturer] = useState<PlateManufacturer | null>(null);
  const [comboPlatePlateManufacturerByGroupKey, setComboPlatePlateManufacturerByGroupKey] = useState<{
    [groupKey: string]: PlateManufacturer | null;
  }>(mapValues(groupedTangiblePlateTypesForComboPlates, () => null));

  const submitDisabled =
    (anyPlatePlatePairs.length > 0 && anyPlatePlateManufacturer === null) ||
    (comboPlatePlatePairs.length > 0 &&
      Object.values(comboPlatePlateManufacturerByGroupKey).some((plateManufacturer) => plateManufacturer === null));
  const downloadQualityControlDigitalQc = () => {
    dispatch(closeCurrentPopup());
    const tangiblePlateTypeMap: { [platePairGuid: string]: PlateManufacturer } = {};
    for (const anyPlatePlatePair of anyPlatePlatePairs) {
      if (!anyPlatePlateManufacturer)
        throw new Error('Attempted to download digital QC file with unspecified tangible plate type');
      tangiblePlateTypeMap[anyPlatePlatePair.guid] = anyPlatePlateManufacturer;
    }
    for (const comboPlatePlatePair of comboPlatePlatePairs) {
      const plateManufacturerForComboPlatePair =
        comboPlatePlateManufacturerByGroupKey[getComboPlateTangiblePlateTypesGroupKey(comboPlatePlatePair)];
      if (!plateManufacturerForComboPlatePair)
        throw new Error('Attempted to download digital QC file with unspecified tangible plate type');
      tangiblePlateTypeMap[comboPlatePlatePair.guid] = plateManufacturerForComboPlatePair;
    }
    dispatch(generateDigitalQcFileForActiveComponentDesign(tangiblePlateTypeMap));
  };

  return (
    <Dialog
      title="Digital QC Download - Select Plate Manufacturer"
      open
      submitText="download"
      variant="design"
      onSubmit={downloadQualityControlDigitalQc}
      submitDisabled={submitDisabled}
      onCancel={() => dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '400px' }}
    >
      {anyPlatePlatePairs.length > 0 && (
        <PropertySelectField
          property="AnyPlate Manufacturer"
          value={anyPlatePlateManufacturer}
          options={anyPlatePlateManufacturers}
          submit={setAnyPlatePlateManufacturer}
        />
      )}
      {orderedGroupedTangiblePlateTypesForComboPlates.map(([groupKey, platePairsForGroup]) => (
        <PropertySelectField
          key={groupKey}
          property={`ComboPlate Manufacturer${
            orderedGroupedTangiblePlateTypesForComboPlates.length > 1
              ? ` (${sortBy(platePairsForGroup, (platePair) => platePair.name)
                  .map((platePair) => platePair.name)
                  .join(', ')})`
              : ''
          }`}
          value={comboPlatePlateManufacturerByGroupKey[groupKey]}
          options={sortBy(
            uniq(
              platePairsForGroup.flatMap((platePair) =>
                platePair.tangiblePlateTypesForComboPlateType.map(getPlateManufacturer),
              ),
            ),
            (plateManufacturer) => plateManufacturer,
          )}
          submit={(plateManufacturer) =>
            void setComboPlatePlateManufacturerByGroupKey((oldState) => ({
              ...oldState,
              [groupKey]: plateManufacturer,
            }))
          }
        />
      ))}
    </Dialog>
  );
}
